import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Music2, Drama, ArrowRight, Sparkles } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { courseTuitionUsd } from "@/lib/courseTuition";

// UK conservatoires — 11 institutions, ~30 degrees. Small enough to
// present as a single flat grid with lightweight discipline chips.
const DISCIPLINES = [
  { key: "music",    label: "Music",           match: (d) => /music|jazz|composition|conducting|classical|popular music/i.test(d.official_title) },
  { key: "drama",    label: "Drama & Acting",  match: (d) => /acting|drama|theatre|directing/i.test(d.official_title) },
  { key: "dance",    label: "Dance",           match: (d) => /dance/i.test(d.official_title) },
  { key: "combined", label: "Design & Production", match: (d) => /design|production|technical|film/i.test(d.official_title) },
];

function DegreeCard({ deg, uni, format }) {
  const offer = deg?.admissions?.offer_rate;
  const tuition = courseTuitionUsd(deg);
  return (
    <Link
      to={`/degree/${deg.id}`}
      data-testid={`conservatoire-degree-card-${deg.id}`}
      className="group border border-stone-200 rounded-lg p-5 bg-white hover:border-stone-500 transition-colors block"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">{uni?.name}</div>
          <div className="font-editorial text-lg mt-1 group-hover:underline underline-offset-4 decoration-stone-400">
            {deg.official_title}
          </div>
          <div className="text-xs text-stone-500 mt-1 font-mono">
            {deg.qualification} · {deg.duration_years} yr · {uni?.city}
          </div>
        </div>
        {typeof offer === "number" && (
          <div className="text-right shrink-0">
            <div className="label-uppercase !text-[9px] text-stone-500">Offer rate</div>
            <div className="font-mono text-lg mt-0.5">{Math.round(offer * 100)}%</div>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Intl fees</div>
          <div className="font-mono mt-0.5">{tuition ? format(tuition) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Cohort</div>
          <div className="font-mono mt-0.5">{uni?.total_students ? uni.total_students.toLocaleString() : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Founded</div>
          <div className="font-mono mt-0.5">{uni?.established ?? "—"}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-purple-800">
        <Music2 className="w-3.5 h-3.5" />
        <span>Audition-based · Master-class teaching · Direct pipeline to industry</span>
      </div>
    </Link>
  );
}


export default function UkConservatoires() {
  const [data, setData] = useState({ degrees: [], universities: {}, total: 0, conservatoires_covered: 0 });
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/lists/uk-conservatoires")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const claimed = new Set();
    const out = [];
    for (const p of DISCIPLINES) {
      const rows = data.degrees.filter((d) => {
        if (claimed.has(d.id)) return false;
        if (p.match(d)) { claimed.add(d.id); return true; }
        return false;
      });
      if (rows.length) out.push({ ...p, rows });
    }
    // "Other" bucket for anything unclaimed
    const other = data.degrees.filter((d) => !claimed.has(d.id));
    if (other.length) out.push({ key: "other", label: "Other pathways", rows: other });
    return out;
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="UK Conservatoires — Music, Drama, Dance, Film · WhichUniversityAdvisor"
        description="The nine UK Conservatoires plus specialist performing-arts schools — Royal College of Music, RADA, Guildhall, Trinity Laban and more. Audition dates, portfolio requirements, tuition and career pathways."
        image="/og/uk-conservatoires.png"
        path="/uk-conservatoires"
      />
      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="conservatoire-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">The audition-first shortlist · UK edition</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight">
            The UK <em>conservatoires</em> — where audition and portfolio beat A-level grades.
          </h1>
          <p className="mt-5 text-stone-700 max-w-2xl leading-relaxed">
            The UK conservatoire tradition is over 200 years old — Royal Academy of Music (1822),
            Guildhall (1880), Royal College of Music (1882), the Royal Conservatoire of Scotland (1847).
            These 11 institutions run on a fundamentally different model to a mainstream university:
            <span className="font-semibold"> audition-based admission</span>, one-to-one master-class
            teaching, 400–1,000 student cohorts, and a direct pipeline to the National Theatre, RSC,
            LSO, Royal Opera House and West End. Every entry below is editorially verified.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Music2 className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">Audition + interview · portfolio-first</span>
            </div>
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Drama className="w-3.5 h-3.5 text-purple-700" />
              <span className="font-underground-sc tracking-wider">Master-class · 1:1 studio teaching</span>
            </div>
          </div>
        </div>

        <aside data-testid="conservatoire-counter" className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6">
          <div className="label-uppercase !text-[10px] text-stone-500">Currently on this list</div>
          <div className="font-editorial text-5xl mt-2">{data.total}</div>
          <div className="text-sm text-stone-600 mt-1">
            editorial degrees across <span className="font-semibold">{data.conservatoires_covered}</span> UK conservatoires
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-stone-500">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Sorted by offer rate — most reachable first.</span>
          </div>
        </aside>
      </div>

      {/* EDITORIAL NOTE */}
      <div className="mt-10 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic"
           data-testid="conservatoire-editorial-note">
        Editorial note: Selectivity here is <span className="italic">not</span> your A-level grade — it's how you play,
        act, dance or create. RADA and Juilliard-adjacent institutions accept &lt;10% of auditionees. Prep the
        audition + interview panel with a specialist coach (WUA+ offers portfolio-review coaching for
        conservatoire applications) — grade thresholds shown are indicative only.
      </div>

      {/* GROUPED DISCIPLINES */}
      {!loading && grouped.map((g) => (
        <section key={g.key} className="mt-14" data-testid={`conservatoire-section-${g.key}`}>
          <div className="flex items-baseline justify-between border-b border-stone-200 pb-3">
            <div>
              <div className="label-uppercase text-stone-500">{g.rows.length} pathway{g.rows.length === 1 ? "" : "s"}</div>
              <h2 className="font-editorial text-2xl sm:text-3xl mt-1">{g.label}</h2>
            </div>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.rows.map((d) => (
              <DegreeCard key={d.id} deg={d} uni={data.universities[d.university_id]} format={format} />
            ))}
          </div>
        </section>
      ))}

      {/* FOOTER CTA */}
      <div className="mt-20 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="label-uppercase text-stone-500">Auditioning next cycle?</div>
          <h3 className="font-editorial text-2xl mt-1">Book a WUA+ portfolio review.</h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            Ex-admissions panel members from RADA, Guildhall and RCM run 1-hour audition-prep sessions. We'll pair you with the right coach for your instrument, voice, dance style or acting reel.
          </p>
        </div>
        <Link
          to="/wua-plus"
          data-testid="conservatoire-cta-wuaplus"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Explore WUA+ <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
